<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, onMounted, computed } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import type { IAoeCart, ICartBatchedRequests, ICartItemCreateCommon } from '../../types/CartTypes'
import type { IProduct } from '../../types/CategoryTypes'
import type { IProductAttributes } from '../../types/SkuTypes'

import {  DropDownList, type DropDownListChangeEvent } from "@progress/kendo-vue-dropdowns";
import { TextBox as KTextBox, type TextBoxChangeEvent } from '@progress/kendo-vue-inputs'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { SvgIcon } from '@progress/kendo-vue-common'
import { circleIcon } from '@progress/kendo-svg-icons'
import { Grid } from '@progress/kendo-vue-grid';

import { GetCategoriesFromLocalStorageOrApi, GetProductsByCategoryAndOrderType } from '../../services/CategoryService'
import { GetFullProductImageDictionary } from '../../services/SkuService.ts'
import Swal from 'sweetalert2'
import { SetIsLoading } from '../../services/AoeHelpers.ts'
import { handleError } from '../../services/ErrorService.ts'
import { AddBatchedItemToCart } from '../../services/CartService'
import { GetOrderTypeCountry } from '../../services/CommonService'
import { GetAvailableQty } from '../../services/InventoryService'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'
import { FormatCurrency } from '../../services/currencyFormatter'

import PricePerUnitWeight from '../PricePerUnitWeight.vue'

const logger = useLogger()

const emit = defineEmits(["update-cart", "update-selected-category"]);

const selectedCategory = ref<CatItem>({ text: '', id: '' });

interface CatItem {
  text: string
  id: string
}

const props = defineProps<{
  cart: IAoeCart | null
  showheader: boolean
  ordertypeid: string
  entryselectedcategory?: string
}>()

const orderTypeId = ref<string>('')
const catDropdownData = ref<CatItem[]>([])
const productLoader = ref<boolean>(false)
const productImageUrls = ref<Record<string, string>>({})
const productAttributesByProductCode = ref<Record<string, IProductAttributes | null>>({})
const updateCart = ref<Record<string, number>>({})
const gridItems = ref<IProduct[]>([])
const showGrid = ref<boolean>(false)
const clearCounter = ref<number>(0)

// Paging state for the grid
const skip = ref(0)
const pageSize = ref(10)

// Computed for paged items
const pagedGridItems = computed(() => {
  return gridItems.value.slice(skip.value, skip.value + pageSize.value)
})


// Handle page change

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onGridPageChange(event: any) {
    logger.debug('onGridPageChange event:', event);
    skip.value = event.page.skip;
    pageSize.value = event.page.take;
    logger.debug('Updated skip:', skip.value, 'pageSize:', pageSize.value, '(from event.page)');
}
const columns = [
        {
          field: 'quantity',
          title: t('partyOrderEntry.QuantityHeader'),
          cell: 'cartItemGridQuantity',
          width: resolveColumnWidth('90px', t('partyOrderEntry.QuantityHeader'))
        },
        {
          field: 'productImage',
          title: t('partyOrderEntry.ProductImageHeader'),
          cell: 'productGridImage',
          headerClassName: 'cart-item-grid-image-header',
          width: resolveColumnWidth('120px', t('partyOrderEntry.ProductImageHeader'))
        },
        {
          field: 'skuCode',
          title: t('partyOrderEntry.CategoryEntryItemHeader'),
          cell: 'productSkuCode',
          width: resolveColumnWidth('120px', t('partyOrderEntry.CategoryEntryItemHeader'))
        },
        {
          field: 'productName',
          title: t('partyOrderEntry.CategoryEntryProductHeader'),
          width: resolveColumnWidth('200px', t('partyOrderEntry.CategoryEntryProductHeader'))
        },
        {
          field: 'unitOfMeasure',
          title: t('partyOrderEntry.CategoryEntryUOMHeader'),
          width: resolveColumnWidth('70px', t('partyOrderEntry.CategoryEntryUOMHeader')),
          cell: 'productGridUOM'
        },
        {
          field: 'stockIndicator',
          title: t('partyOrderEntry.PSHeader'),
          width: resolveColumnWidth('70px', t('partyOrderEntry.PSHeader')),
          cell: 'productGridStockIndicator',
          headerClassName: 'product-grid-ps-header',
        },
        {
          field: 'retailPrice',
          title: t('partyOrderEntry.CategoryEntryRetailHeader'),
          format: '{0:c}',
          cell: 'productRetailPrice',
          width: resolveColumnWidth('110px', t('partyOrderEntry.CategoryEntryRetailHeader')),
        },
        {
          field: 'buyingDiscountAmount',
          title: t('partyOrderEntry.CategoryEntryBuyingDiscountHeader'),
          format: '{0:c}',
          cell: 'productBuyingDiscount',
          width: resolveColumnWidth('120px', t('partyOrderEntry.CategoryEntryBuyingDiscountHeader')),
        },
        {
          field: 'yourPrice',
          title: t('partyOrderEntry.CategoryEntryYourPriceHeader'),
          format: '{0:c}',
          cell: 'productGridYourPrice',
          width: resolveColumnWidth('140px', t('partyOrderEntry.CategoryEntryYourPriceHeader')),
        }
      ]

async function addToCartClickedHandler(event: MouseEvent) {
  logger.debug('addToCartClickedHandler', event)

  if (props.cart == null || Object.keys(updateCart.value).length === 0) {
    return;
  }

  SetIsLoading(true)

  try {
    // Check stock availability for all items before adding to cart
    const unavailableItems: string[] = [];

    for (const [skuCode, quantity] of Object.entries(updateCart.value)) {
      if (quantity > 0) {
        const availableQty = await GetAvailableQty(orderTypeId.value, skuCode);

        if (availableQty < quantity) {
          // Item is not available in requested quantity
          const product = gridItems.value.find(p =>
            p.skus && p.skus.some(s => s.skuCode === skuCode)
          );

          const productName = product ? product.productName : skuCode;
          unavailableItems.push(productName);
        }
      }
    }

    if (unavailableItems.length > 0) {
      // Show error for unavailable items
      await Swal.fire({
        title: t('alerts.AddToCartError'),
        html: t('alerts.NoQuantityAvailable') + '<br><br>' +
              unavailableItems.join('<br>'),
        icon: 'error',
        confirmButtonText: t('buttons.Okay')
      });

      SetIsLoading(false);
      return;
    }

    // Check host-only restriction for all items before adding to cart
    if (!props.cart?.isHostCart) {
      const hostOnlyItems: string[] = []
      for (const [skuCode, quantity] of Object.entries(updateCart.value)) {
        if (quantity > 0) {
          const product = gridItems.value.find(p =>
            p.skus && p.skus.some(s => s.skuCode === skuCode)
          )
          const sku = product?.skus?.find(s => s.skuCode === skuCode)
          if (sku?.onlyAvailableForHost) {
            hostOnlyItems.push(product?.productName ?? skuCode)
          }
        }
      }

      if (hostOnlyItems.length > 0) {
        await Swal.fire({
          title: t('alerts.HostOnlyItemTitle'),
          html: t('alerts.HostOnlyItemMessage') + '<br><br>' + hostOnlyItems.join('<br>'),
          icon: 'error',
          confirmButtonText: t('buttons.Okay')
        })
        SetIsLoading(false)
        return
      }
    }

    // Check discount-only restriction for all items before adding to cart
    const discountOnlyItems: string[] = []
    for (const [skuCode, quantity] of Object.entries(updateCart.value)) {
      if (quantity > 0) {
        const product = gridItems.value.find(p =>
          p.skus && p.skus.some(s => s.skuCode === skuCode)
        )
        const sku = product?.skus?.find(s => s.skuCode === skuCode)
        if (sku?.onlyAvailableForDiscount) {
          discountOnlyItems.push(product?.productName ?? skuCode)
        }
      }
    }

    if (discountOnlyItems.length > 0) {
      await Swal.fire({
        title: t('alerts.DiscountOnlyItemTitle'),
        html: t('alerts.DiscountOnlyItemMessage') + '<br><br>' + discountOnlyItems.join('<br>'),
        icon: 'error',
        confirmButtonText: t('buttons.Okay')
      })
      SetIsLoading(false)
      return
    }

    // All items are available, proceed with adding to cart
    const batchedRequests: ICartBatchedRequests = {
      aoeCartIdToken: props.cart.aoeCartIdToken,
      batchedRequests: [],
    }

    for (const [key, value] of Object.entries(updateCart.value)) {
      if (value > 0) {
        const cartItemCreateCommon: ICartItemCreateCommon = {
          skuCode: key,
          quantity: value,
        }
        batchedRequests.batchedRequests.push({
          cartItemCreate: cartItemCreateCommon,
        })
      }
    }

    if (batchedRequests.batchedRequests.length > 0) {
      await AddBatchedItemToCart(batchedRequests);
    }
  } catch (error) {
    await handleError(error, t);
  } finally {
    // Clear quantities and input fields on both success and failure
    updateCart.value = {};
    clearCounter.value++;
    SetIsLoading(false);
    emit("update-cart", event);
  }
}

onMounted(async () => {
  logger.debug('onMounted', 'AoeCategoryEntry')

  // Set orderTypeId from prop if provided
  if (props.ordertypeid) {
    orderTypeId.value = props.ordertypeid
  }

  orderTypeId.value = await GetOrderTypeCountry(orderTypeId.value)

  GetCategoriesFromLocalStorageOrApi({orderTypeId: orderTypeId.value}).then((categories) => {
    logger.debug('GetCategoriesFromLocalStorageOrApi', categories)
    if (categories != null)
    {
      for (const cats of categories)
      {
        const catItem = categories.filter(x => x.category.categoryCode == cats.category.categoryCodeParent)
        if (catItem.length == 0)
        {
          catDropdownData.value.push({ text: cats.category.categoryName, id: cats.category.categoryCode })

          if (cats.categoryChildren != null)
          {
            for (const catChild of cats.categoryChildren)
            {
              catDropdownData.value.push({ text: t('labels.categoryPrefix') + catChild.categoryName, id: catChild.categoryCode })
            }
          }
        }
      };

      if (props.entryselectedcategory != undefined && props.entryselectedcategory.length > 0)
      {
        const selected : CatItem = catDropdownData.value.filter(x => x.id == props.entryselectedcategory)[0]
        selectedCategory.value = { text: selected.text, id: props.entryselectedcategory }
        onCategoryChange(null);
      }
    }
  }).catch((error) => {
    logger.error('GetCategoriesFromLocalStorageOrApi', error)
  })
})

function onCategoryChange(event: DropDownListChangeEvent | null) {
  skip.value = 0; // Reset paging to first page on category change
  logger.debug('onCategoryChange', event)
  if (event != null)
  {
    selectedCategory.value = event.value
    emit('update-selected-category', selectedCategory.value.id)
  }

  GetProductsByCategoryAndOrderType(
    { orderTypeId: orderTypeId.value,
      categoryCode: selectedCategory.value.id,
      includeInventoryLevels: true,
      beeNumber: props.cart == null ? null : props.cart.beeNumberCustomer
    }).then((products) => {
    logger.debug('GetProductsByCategoryAndOrderType', products)
    if (products != null)
    {
        skip.value = 0; // Defensive: always reset paging after loading new products
        showGrid.value = true
        gridItems.value = products.item.products.filter(p =>
          !p.skus || p.skus.length === 0 ||
          (!p.skus[0].onlyAvailableForDiscount &&
            (props.cart?.isHostCart || !p.skus[0].onlyAvailableForHost))
        )
        GetProductImageUrls(products.item.products).then((pIUrls) => {
          logger.debug('GetProductImageUrls', pIUrls)
          productImageUrls.value = pIUrls
        })

        const next: Record<string, IProductAttributes | null> = {}
        for (const prod of products.item.products ?? []) {
          const raw = prod?.attributes?.PricePerUnitWeight
          const parsed = raw != null ? Number(raw) : NaN
          if (Number.isFinite(parsed) && parsed > 0) {
            next[prod.productCode] = { CatalogImage: '', PricePerUnitWeight: parsed }
          } else {
            next[prod.productCode] = null
          }
        }
        productAttributesByProductCode.value = next
    }
  }).catch((error) => {
    logger.error('GetProductsByCategoryAndOrderType', error)
  })
}

function cartItemQuantityInputChange(event: TextBoxChangeEvent, skuCode: string) {
  logger.debug('cartItemQuantityInputChange', event, skuCode)

  if (!Object.keys(updateCart.value).includes(skuCode.toString()))
  {
    updateCart.value[skuCode] = parseInt(event.value as string)
  }
  else
  {
    const qty = parseInt(event.value as string)
    updateCart.value[skuCode] = qty
  }
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug('GetFullProductImageUrlFromStringDictionary', productCode, productImageUrls.value)
  const productImageUrl = productImageUrls.value[productCode]
  if (productImageUrl) return productImageUrl
  return '#'
}

async function GetProductImageUrls(products: IProduct[]) : Promise<Record<string, string>>
{
  const uniqueProductCodes = Array<string>()
  for(const prod of products) {
    if (!uniqueProductCodes.includes(prod.productCode)) {
      uniqueProductCodes.push(prod.productCode)
    }
  }
  const pIUrls = await GetFullProductImageDictionary(uniqueProductCodes)
  return pIUrls
}

function formatCurrency(value : number) {
  // Use the centralized FormatCurrency function from currencyFormatter.ts
  // Pass the currencyId from the cart, or null if not available
  const currencyId = props.cart?.currencyId || null
  return FormatCurrency(value, currencyId)
}

function getYourPrice(prod: IProduct): number | null {
  if (!prod.skus || prod.skus.length === 0) return null
  const sku = prod.skus[0]
  const buyingDiscount = sku.buyingDiscount != undefined ? sku.buyingDiscount : 0
  return sku.retailPrice - buyingDiscount
}
</script>

<template>
  <div class="aoe-category-entry">
    <div v-if="props.showheader" class="row party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div category-entry-section-header">
        {{ t('partyOrderEntry.CategoryEntryHeader') }}
      </div>
    </div>
    <div class="row choose-category-row mt-3">
      <div class="col-12 col-md-2">
        {{ t('partyOrderEntry.Category') }}
      </div>
      <div class="col-12 col-md-10">
        <DropDownList
          :data-items="catDropdownData"
          :text-field="'text'"
          :data-item-key="'id'"
          :value="selectedCategory"
          @change="onCategoryChange($event)"
        >
        </DropDownList>
      </div>
    </div>
    <div v-if="showGrid"  class="row add-to-cart-row mt-3">
      <div class="col-12 add-to-cart-button-div">
        <KButton
          @click="addToCartClickedHandler($event)"
          class="baseBtn primaryBtn single-line-add-to-cart-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'">
          {{ t('labels.AddToCart') }}
        </KButton>
      </div>
    </div>
    <div v-if="showGrid"  class="row stock-indicator-key-row mt-3">
      <div class="col-6 col-md-3 stock-indicator-key-div">
        <SvgIcon
          class="product-stock-indicator inventory-available"
          :icon="circleIcon">
        </SvgIcon>
        {{ t('partyOrderEntry.Available') }}
      </div>
      <div class="col-6 col-md-3 stock-indicator-key-div">
        <SvgIcon
          class="product-stock-indicator inventory-low-stock"
          :icon="circleIcon">
        </SvgIcon>
        {{ t('partyOrderEntry.LowStock') }}
      </div>
      <div class="col-6 col-md-3 stock-indicator-key-div">
        <SvgIcon
          class="product-stock-indicator inventory-out-of-stock"
          :icon="circleIcon">
        </SvgIcon>
        {{ t('partyOrderEntry.OutOfStock') }}
      </div>
      <div class="col-6 col-md-3 stock-indicator-key-div">
        <SvgIcon
          class="product-stock-indicator inventory-stop-sell"
          :icon="circleIcon">
        </SvgIcon>
        {{ t('partyOrderEntry.StopSell') }}
      </div>
    </div>
    <div v-if="showGrid" class="row mt-3 category-product-grid-row">
      <div class="col-12 category-product-grid-div">
        <Grid
          ref="gridProducts"
          :data-items="pagedGridItems"
          :resizable="true"
          :total="gridItems.length"
          :skip="skip"
          :pageable="{ buttonCount: 5, pageSizes: true, info: true }"
          :pageSize="pageSize"
          @pagechange="onGridPageChange"
          class="category-product-grid"
          :columns="columns"
          :loader="productLoader">
          <template v-slot:productGridImage="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-image">
              <img
                  :src="GetFullProductImageUrlFromStringDictionary(props.dataItem.productCode)"
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"/>
            </td>
          </template>
          <template v-slot:productSkuCode="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-sku-code">
              {{ props.dataItem.skus.length > 0 ? props.dataItem.skus[0].skuCode : '' }}
            </td>
          </template>
          <template v-slot:productRetailPrice="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-retail-price">
              {{ props.dataItem.skus.length > 0 && props.dataItem.skus[0].pricingNotFound ? t('partyOrderEntry.MissingPricing') : '' }}
              {{ props.dataItem.skus.length > 0 ? formatCurrency(props.dataItem.skus[0].retailPrice) : '' }}
            </td>
          </template>
          <template v-slot:productBuyingDiscount="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-buying-discount">
              {{ props.dataItem.skus.length > 0 && props.dataItem.skus[0].buyingDiscount != undefined ? formatCurrency(props.dataItem.skus[0].buyingDiscount) : formatCurrency(0) }}
            </td>
          </template>
          <template v-slot:cartItemGridQuantity="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-quantity">
              <KTextBox
                :key="clearCounter"
                :value="props.dataItem.quantity !=  updateCart[props.dataItem.lineNumber] &&
                        updateCart[props.dataItem.lineNumber] != undefined &&
                        updateCart[props.dataItem.lineNumber] != 0 ? updateCart[props.dataItem.lineNumber] : props.dataItem.quantity"
                @input="cartItemQuantityInputChange($event, props.dataItem.skus[0].skuCode)"
                class="cart-item-grid-quantity text-center"
              />
            </td>
          </template>
          <template v-slot:productGridUOM="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-uom">
              {{ props.dataItem.skus.length > 0 ? props.dataItem.skus[0].unitofMeasureName + ' (' + props.dataItem.skus[0].unitofMeasureConversion + ')' : '' }}
            </td>
          </template>
          <template v-slot:productGridStockIndicator="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-stock-indicator">
              <SvgIcon
                v-if="props.dataItem.skus.length > 0 && (props.dataItem.skus[0].inventoryLevel.inventoryLevel == 'Available' || props.dataItem.skus[0].outOfStockVisible)"
                class="product-stock-indicator inventory-available"
                :icon="circleIcon">
              </SvgIcon>
              <SvgIcon
                v-if="props.dataItem.skus.length > 0 && props.dataItem.skus[0].inventoryLevel.inventoryLevel == 'LowStock' && !props.dataItem.skus[0].outOfStockVisible"
                class="product-stock-indicator inventory-low-stock"
                :icon="circleIcon">
              </SvgIcon>
              <SvgIcon
                v-if="props.dataItem.skus.length > 0 && props.dataItem.skus[0].inventoryLevel.inventoryLevel == 'OutOfStock' && !props.dataItem.skus[0].outOfStockVisible"
                class="product-stock-indicator inventory-out-of-stock"
                :icon="circleIcon">
              </SvgIcon>
              <SvgIcon
                v-if="props.dataItem.skus.length > 0 && props.dataItem.skus[0].inventoryLevel.inventoryLevel == 'StopSell' && !props.dataItem.skus[0].outOfStockVisible"
                class="product-stock-indicator inventory-stop-sell"
                :icon="circleIcon">
              </SvgIcon>
            </td>
          </template>
          <template v-slot:productGridYourPrice="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-subtotal">
              {{ props.dataItem.skus.length > 0 ? formatCurrency(props.dataItem.skus[0].retailPrice - (props.dataItem.skus[0].buyingDiscount != undefined ? props.dataItem.skus[0].buyingDiscount : 0)) : formatCurrency(0) }}
              <div>
                <PricePerUnitWeight
                  :RetailPrice="props.dataItem.skus.length > 0 ? props.dataItem.skus[0].retailPrice : null"
                  :SalePrice="getYourPrice(props.dataItem)"
                  :IProductAttributes="productAttributesByProductCode[props.dataItem.productCode] ?? null"
                  :RenderAsTwoLines="true"
                />
              </div>
            </td>
          </template>
        </Grid>
      </div>
    </div>
    <div v-if="showGrid"  class="row add-to-cart-row mt-3">
      <div class="col-12 add-to-cart-button-div">
        <KButton
          @click="addToCartClickedHandler($event)"
          class="baseBtn primaryBtn single-line-add-to-cart-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'">
          {{ t('labels.AddToCart') }}
        </KButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.add-to-cart-button-div {
  text-align: right;
}

.product-grid-stock-indicator,
.stock-indicator-key-div
{
  text-align: center !important;
}

.product-grid-ps-header .k-column-title
{
  margin-left: auto;
  margin-right: auto;
}

.stock-indicator-key-row
{
  border: 1px solid var(--kendo-color-border);
}

.product-grid-subtotal {
  white-space: normal;
}

</style>
